import React from 'react';
import classNames from 'classnames';
import {
	ListItem,
	ListItemText,
	ListItemAvatar,
	ListItemSecondaryAction,
	Avatar,
	IconButton,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import imagePlaceholder from 'common/assets/image-placeholder.svg';
import { useTranslation } from 'services/localization';
import { RiLock2Fill, RiArrowUpLine, RiArrowDownLine } from 'react-icons/ri';
import { useFlash } from 'hooks/useFlash';

interface Props {
	id: string;
	label: string;
	image?: string;
	onMoveUp: (id: string) => void;
	canMoveUp: boolean;
	onMoveDown: (id: string) => void;
	canMoveDown: boolean;
	disabled?: boolean;
	disabledTooltip?: string;
	hideImage?: boolean;
}

const OrderableListItem = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();

	const {
		id,
		label,
		image,
		onMoveUp,
		canMoveUp,
		onMoveDown,
		canMoveDown,
		disabled,
		disabledTooltip,
		hideImage,
	} = props;

	const [isFlashing, flash] = useFlash();

	const handleMoveUp = () => {
		onMoveUp?.(id);
		flash();
	};

	const handleMoveDown = () => {
		onMoveDown?.(id);
		flash();
	};

	const renderActions = () => {
		if (disabled) {
			return (
				<ListItemSecondaryAction>
					<RiLock2Fill />
				</ListItemSecondaryAction>
			);
		}

		return (
			<ListItemSecondaryAction>
				<IconButton
					disabled={!canMoveUp}
					aria-label={t('common:actions.moveUp', 'Move up')}
					onClick={handleMoveUp}
					size="large"
					disableRipple
				>
					<RiArrowUpLine />
				</IconButton>
				<IconButton
					disabled={!canMoveDown}
					aria-label={t('common:actions.moveDown', 'Move down')}
					onClick={handleMoveDown}
					size="large"
					disableRipple
				>
					<RiArrowDownLine />
				</IconButton>
			</ListItemSecondaryAction>
		);
	};

	return (
		<ListItem
			className={classNames(classes.item, { [classes.flashing]: isFlashing })}
			divider
			disabled={disabled}
			sx={{ paddingRight: '120px' }}
		>
			{!hideImage && (
				<ListItemAvatar>
					<Avatar variant="square" src={image ?? imagePlaceholder} alt={label} />
				</ListItemAvatar>
			)}
			<ListItemText
				primary={label}
				primaryTypographyProps={{
					className: classes.textEllipsis,
				}}
				secondary={
					disabled
						? disabledTooltip ??
						  t('products.notAllowedToEditThisRow', 'Not allowed to edit this row')
						: ''
				}
			/>
			{renderActions()}
		</ListItem>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		list: {
			width: '100%',
		},
		item: {
			borderRadius: '4px',
			border: `1px solid ${theme.palette.border.paper}`,
			marginTop: '4px',
			transition: 'background 0.3s',
		},
		flashing: {
			background: theme.palette.action.focus,
		},
		pointerEventsNone: {
			pointerEvents: 'none',
		},
		textEllipsis: {
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
	}),
);

export default OrderableListItem;
